import React, { useEffect, useMemo, useRef, useState } from "react";
import ActivityMetrics from "../../pages/landlord-metrics/components/ActivityMetrics";
import { ActivityMetricsSkeleton } from "./SkeletonLoaders";
import { getLandlordActivityMetrics, getLandlordFunnelMetrics } from "../../context/functions/AdminFunction";
import "../../styles/tenant-metrics.css";

const LandlordMetricsSection = () => {
  const defaults = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return { start: formatDate(start), end: formatDate(end) };
  }, []);

  const [dateRange, setDateRange] = useState({
    startDate: defaults.start,
    endDate: defaults.end,
  });
  const [showDateRangeMenu, setShowDateRangeMenu] = useState(false);
  const [isCustomDateMode, setIsCustomDateMode] = useState(false);
  const rangeRef = useRef(null);

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summaryMetrics, setSummaryMetrics] = useState({});

  const fetchSummaryMetrics = async (startDate = "", endDate = "") => {
    setLoadingSummary(true);
    try {
      const [activityRes, funnelRes] = await Promise.all([
        getLandlordActivityMetrics({ startDate, endDate }),
        getLandlordFunnelMetrics({ startDate, endDate }),
      ]);

      const nextSummary = {};
      if (activityRes?.success && activityRes?.data) {
        Object.assign(nextSummary, activityRes.data);
      }
      if (funnelRes?.success && funnelRes?.data) {
        nextSummary.funnelProfiles = funnelRes.data.landlords_profiles;
        nextSummary.funnelActivated = funnelRes.data.landlords_activated;
        nextSummary.funnelAvgTimeDays = funnelRes.data.avg_time_to_first_core_action_days;
        nextSummary.funnelAvgTimeMilliseconds = funnelRes.data.avg_time_to_first_core_action_ms;
        nextSummary.funnelActivationRate = funnelRes.data.activation_rate_percentage;
      }

      setSummaryMetrics(nextSummary);
    } catch (error) {
      console.error("Failed to fetch landlord metrics", error);
      setSummaryMetrics({});
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    fetchSummaryMetrics(dateRange.startDate, dateRange.endDate);
  }, []);

  useEffect(() => {
    fetchSummaryMetrics(dateRange.startDate, dateRange.endDate);
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rangeRef.current && !rangeRef.current.contains(event.target)) {
        setShowDateRangeMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const applyLast30Days = () => {
    setDateRange({ startDate: defaults.start, endDate: defaults.end });
    setIsCustomDateMode(false);
    setShowDateRangeMenu(false);
  };

  const applyAllTime = () => {
    setDateRange({ startDate: "", endDate: "" });
    setIsCustomDateMode(false);
    setShowDateRangeMenu(false);
  };

  const getDateRangeLabel = () => {
    if (dateRange.startDate === defaults.start && dateRange.endDate === defaults.end) {
      return "Last 30 Days";
    }
    if (!dateRange.startDate && !dateRange.endDate) return "All Time";
    return "Custom";
  };

  return (
    <div className="mt-5 mb-5">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3 gap-3">
        <h4 style={{ fontWeight: 600, color: "#2c3e50", margin: 0 }}>Landlord Metrics</h4>
      </div>
      <div className="tenant-filter-panel mb-4">
        <div className="tenant-filter-row">
          <div className="tenant-filter-group" ref={rangeRef}>
            <button
              type="button"
              className={`range-chip ${showDateRangeMenu ? "range-chip-filter-active" : ""}`}
              onClick={() => setShowDateRangeMenu((prev) => !prev)}
            >
              {getDateRangeLabel()}
            </button>
            {showDateRangeMenu && (
              <div className="range-menu">
                <button type="button" className="range-menu-item" onClick={applyLast30Days}>
                  Last 30 Days
                </button>
                <button type="button" className="range-menu-item" onClick={applyAllTime}>
                  All Time
                </button>
                <button
                  type="button"
                  className="range-menu-item"
                  onClick={() => {
                    setIsCustomDateMode(true);
                    setShowDateRangeMenu(false);
                  }}
                >
                  Custom
                </button>
              </div>
            )}
          </div>
        </div>
      {isCustomDateMode && (
        <div className="d-flex align-items-center gap-2 flex-wrap mt-2 mb-3">
          <input
            type="date"
            name="startDate"
            value={dateRange.startDate}
            onChange={handleDateChange}
            className="date-input compact-date-input"
          />
          <input
            type="date"
            name="endDate"
            value={dateRange.endDate}
            onChange={handleDateChange}
            className="date-input compact-date-input"
          />
        </div>
      )}
      </div>

      {loadingSummary ? (
        <ActivityMetricsSkeleton />
      ) : (
        <ActivityMetrics summaryMetrics={summaryMetrics} loadingSummary={false} />
      )}
    </div>
  );
};

export default LandlordMetricsSection;
